import { createGraphqlServiceWithTransform, type UseQueryResult } from '@repo/services';
import { useDispatch } from 'react-redux';
import { useDeepCompareEffect } from 'use-deep-compare';

import {
  type ActivitiesType,
  getReferencedPropertiesQuery, type GetReferencedPropertiesType, type LeadAttributesType, type UsersType,
} from '../queries/referencedConfigService';
import { setConfigDetails } from '../stores';

export interface ReferencedConfigInputType {
  leadAttributes?: string[];
  leadAttributeOptions?: number[];
  users?: string[];
  activities?: number[];
  salesGroups?: number[];
  tasks?: number[];
}

const {
  useLazyGetGraphqlDataQuery,
} = createGraphqlServiceWithTransform<GetReferencedPropertiesType>(
  'CONFIG_QUERY',
  (response, dispatch) => {
    const transformedResponse = {
      users: response.configData.users?.reduce((acc, user) => {
        acc[user.userUuid] = user;
        return acc;
      }, {} as Record<string, UsersType>),
      leadAttributes: response.configData.leadAttributes?.reduce((acc, leadAttribute) => {
        acc[leadAttribute.schemaName] = leadAttribute;
        return acc;
      }, {} as Record<string, LeadAttributesType>),
      activities: response.configData.activities?.reduce((acc, activity) => {
        acc[activity.key] = activity;
        return acc;
      }, {} as Record<string, ActivitiesType>),
      salesGroups: response.configData.salesGroups,
      tasks: response.configData.tasks,
      leadAttributeOptions: response.configData.leadAttributeOptions,
    };
    dispatch(setConfigDetails(transformedResponse));
    return response;
  },
);

const useFetchConfigDetails = (
  configData: ReferencedConfigInputType,
  // Be careful with this function, it should not trigger on every render
  callbackToUpdateFilter?: () => void,
) => {
  const dispatch = useDispatch();

  const [fetchConfigDetails,
    { isFetching: isFetchingConfigDetails },
  ] = useLazyGetGraphqlDataQuery<UseQueryResult<GetReferencedPropertiesType>>();

  useDeepCompareEffect(() => {
    if (
      configData.leadAttributes?.length
      || configData.users?.length
      || configData.activities?.length
      || configData.salesGroups?.length
      || configData.tasks?.length
      || configData.leadAttributeOptions?.length
    ) {
      fetchConfigDetails({
        document: getReferencedPropertiesQuery,
        dispatch,
        variables: {
          leadAttributeSchemaNames: configData.leadAttributes,
          userUuids: configData.users,
          activityIds: configData.activities,
          salesGroupIds: configData.salesGroups,
          taskTemplateIds: configData.tasks,
          leadAttributeOptionIds: configData.leadAttributeOptions,
        },
      }, true).then(() => {
        callbackToUpdateFilter?.();
      });
    }
  }, [configData, dispatch, fetchConfigDetails, callbackToUpdateFilter]);

  return {
    isLoading: isFetchingConfigDetails,
  };
};

export default useFetchConfigDetails;
